import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { PopupService } from '../../services/popup.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Platform } from '@ionic/angular';

declare var cordova: any;

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnDestroy {

  @Input() url : string = ""
  showLoading : Boolean = true
  sanitizeUrl;
  private viewportMeta: HTMLMetaElement;
  private originalViewportContent: string;
  zoom: number = 1.0; // Current zoom level
  zoomStep: number = 0.25; // Zoom step increment

  constructor(
    private sanitizer: DomSanitizer,
    private popupService : PopupService,
    private platform: Platform) { }

  ngOnInit() {

    if(this.url){
      this.sanitizeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
    }

    // Enable zoom for PDF viewing on mobile
    if (this.platform.is('mobile') || this.platform.is('mobileweb')) {
      this.enableZoom();
    }
  }

  ngOnDestroy() {
    // Restore original viewport settings when leaving the PDF viewer
    if (this.viewportMeta && this.originalViewportContent) {
      this.viewportMeta.setAttribute('content', this.originalViewportContent);
    }
  }

  enableZoom() {
    this.viewportMeta = document.querySelector('meta[name="viewport"]');
    if (this.viewportMeta) {
      this.originalViewportContent = this.viewportMeta.getAttribute('content');
      // Enable user scaling for PDF viewing
      this.viewportMeta.setAttribute('content', 
        'viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0, user-scalable=yes');
    }
  }

  afterLoadComplete() {
    // Intercept clicks on PDF links to open in external system browser
    setTimeout(() => {
      this.setupLinkInterceptor();
    }, 100);
  }

  setupLinkInterceptor() {
    const annotationLayer = document.querySelector('.annotationLayer');
    if (!annotationLayer) {
      // Retry if annotation layer not ready
      setTimeout(() => this.setupLinkInterceptor(), 100);
      return;
    }

    // Use event delegation on the container for better reliability
    annotationLayer.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      
      if (link && link.href) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        const href = link.href;
        console.log('Link clicked, opening in external browser:', href);
        this.openExternalLink(href);
      }
    }, true);

    // Also remove href to prevent default navigation, store in data attribute
    const links = annotationLayer.querySelectorAll('a');
    links.forEach((link: HTMLAnchorElement) => {
      const originalHref = link.href;
      link.setAttribute('data-external-url', originalHref);
      link.removeAttribute('href');
      link.style.cursor = 'pointer';
      
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        const url = link.getAttribute('data-external-url');
        if (url) {
          console.log('Opening external URL:', url);
          this.openExternalLink(url);
        }
      }, true);
    });
  }

  openExternalLink(url: string) {
    console.log('Attempting to open in system browser:', url);
    
    if (this.platform.is('cordova') && typeof cordova !== 'undefined') {
      // Use cordova.exec to call the native browser directly, bypassing InAppBrowser
      if (this.platform.is('android')) {
        // Android: Use cordova.exec to launch browser intent
        cordova.exec(
          null, 
          null, 
          'InAppBrowser', 
          'open', 
          [url, '_system', 'location=yes']
        );
      } else if (this.platform.is('ios')) {
        // iOS: Use cordova.exec to open in Safari
        cordova.exec(
          null, 
          null, 
          'InAppBrowser', 
          'open', 
          [url, '_system', 'location=yes']
        );
      }
    } else {
      // Web fallback
      window.open(url, '_blank');
    }
  }

  textLayerRendered() {
    this.showLoading = false;
  }

  onError() {
    this.showLoading = false;
    this.popupService.showModal({ 
      heading: 'Error', 
      message: 'Failed to load the document. Please try again.',
      btn: 'Dismiss', 
      navigateRoute: null,
      imgURL: 'assets/images/sorry1.png' 
    });

  }

  onProgress() {
    this.showLoading = true;
  }

  zoomIn() {
    if (this.zoom < 3.0) { // Max zoom 300%
      this.zoom += this.zoomStep;
    }
  }

  zoomOut() {
    if (this.zoom > 0.5) { // Min zoom 50%
      this.zoom -= this.zoomStep;
    }
  }

  resetZoom() {
    this.zoom = 1.0; // Reset to 100%
  }


}